import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import classes from "./Lightbox.module.css";

/**
 * Full-screen photo viewer for the gallery page.
 *
 * Steps through the album with the arrow keys or the on-screen controls, and
 * closes on Escape or a click outside the image.
 *
 * Portalled into <body> for the same reason as Modal: a transformed or
 * overflow-hidden ancestor would otherwise contain and clip this fixed layer.
 */
export default function Lightbox({ photos, startIndex = 0, title, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const figureRef = useRef(null);

  const count = photos.length;
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, next, prev]);

  // Hold the page behind the viewer still, compensating for the scrollbar so
  // the layout underneath does not jump.
  useEffect(() => {
    const { body, documentElement } = document;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    const scrollbar = window.innerWidth - documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, []);

  useLayoutEffect(() => {
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !figureRef.current) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(figureRef.current, {
        opacity: 0,
        scale: 0.97,
        duration: 0.3,
        ease: "power3.out",
      });
    });
    return () => ctx.revert();
  }, [index]);

  return createPortal(
    <div
      className={classes.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title ? `${title} — photo viewer` : "Photo viewer"}
    >
      <button type="button" className={classes.close} onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      {count > 1 && (
        <button
          type="button"
          className={`${classes.nav} ${classes.prev}`}
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous photo"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <figure
        className={classes.figure}
        ref={figureRef}
        onClick={(e) => e.stopPropagation()}
      >
        <img src={photos[index]?.url} alt={`${title ?? "Gallery"} ${index + 1}`} />
        <figcaption className={classes.caption}>
          {title && <span className={classes.captionTitle}>{title}</span>}
          <span className={classes.counter}>
            {index + 1} / {count}
          </span>
        </figcaption>
      </figure>

      {count > 1 && (
        <button
          type="button"
          className={`${classes.nav} ${classes.next}`}
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next photo"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>,
    document.body
  );
}
